import React, { useState, useEffect } from 'react';
import useDebounce from 'hooks/use-debounce';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import SuggestedLocationDropdown from 'components/suggested-location-dropdown';
import { drawInitialMap, query, getDirectionAndAddOverlays } from 'utils/apple-maps';
import { pluralizeWithCount } from '@zoocasa/node-kit/strings/pluralize';
import TextInput from '@zoocasa/node-kit/components/controls/text-input';
import Button from 'components/control/button';
import styles from './style.module.scss';
import { useThemeContext } from 'contexts';
import { buildClassName } from 'utils/build-class-name';
import type Listing from 'data/listing';
import type SearchPrediction from 'data/search-predictions';
import { ThemeNames } from 'types/themes';

interface Props {
  listing: Listing;
}

export default function TravelTime({ listing }: Props) {
  const [map, setMap] = useState(null);
  const [destinationName, setDestinationName] = useState('');
  const [isDriving, setIsDriving] = useState(true);
  const [isLoadingSearchPredictions, setIsLoadingSearchPredictions] = useState(false);
  const [locationPredictions, setLocationPredictions] = useState<SearchPrediction[]>([]);
  const [isPredictionDropdownOpen, setIsPredictionDropdownOpen] = useState(false);
  const [distance, setDistance] = useState<number|undefined>();
  const [travelTime, setTravelTime] = useState<number|undefined>();
  const { themeName } = useThemeContext();
  const isZoocasaTheme = themeName === ThemeNames.ZOOCASA;

  const { position, streetAddress, city, province, id } = listing;
  const listingAddress = `${streetAddress}, ${city}, ${province}`;
  const mapElementId = `${id}-travel-time`;

  const closeSearchDropdown = () => setIsPredictionDropdownOpen(false);
  const debouncedSearch = useDebounce(() => runLocationSearch(), 400);

  useEffect(() => {
    if (!map) {
      drawInitialMap(mapElementId, position, false, themeName as ThemeNames).then(setMap);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (destinationName.length) {
      getDirections();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriving]);

  const runLocationSearch = async () => {
    setIsLoadingSearchPredictions(true);
    const searchResults = await query(destinationName, true);
    setIsLoadingSearchPredictions(false);
    setLocationPredictions(searchResults);
    setIsPredictionDropdownOpen(true);
  };

  const handleInput = (value: string) => {
    setDestinationName(value);
    debouncedSearch();
  };

  const handleLocationClick = async (searchPrediction: SearchPrediction) => {
    const destinationAddress = searchPrediction.description;
    setDestinationName(destinationAddress);
    getDirections(destinationAddress);
    setIsPredictionDropdownOpen(false);
  };

  const getDirections = async (address?: string) => {
    const directionInformation: Record<string, number> = await getDirectionAndAddOverlays(map, address || destinationName, listingAddress, isDriving);
    setDistance(directionInformation.distance);
    setTravelTime(directionInformation.expectedTravelTime);
  };

  const secondsFormatter = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds - (hours * 3600)) / 60);
    return `${pluralizeTime(hours, 'Hour')} ${pluralizeTime(minutes, 'Minute')}`;
  };

  const pluralizeTime = (time: number, unit: string) => time === 0 ? '' : pluralizeWithCount(time, unit);

  return (
    <div className={styles.component}>
      <div className={styles['input-row']}>
        <div className={styles.direction}>
          <span/>
          <span/>
          <span/>
          <span/>
          <span/>
        </div>
        <section className={styles['input-container']}>
          <div className={styles.destination}>
            Starting Address
            <TextInput value={streetAddress!} disabled={true} />
          </div>
          <div ref={useOutsideClickHandler(closeSearchDropdown) as any}>
            Destination Address
            <div>
              <TextInput
                value={destinationName}
                placeholder='Enter an address'
                onValueChange={handleInput}
              />
              <SuggestedLocationDropdown
                isLoading={isLoadingSearchPredictions}
                searchPredictions={locationPredictions}
                setActivePrediction={() => {}}
                onClick={handleLocationClick}
                isActive={isPredictionDropdownOpen}
                className={styles['suggested-location-dropdown']}
                searchAgentPredictions={[]}
              />
            </div>
          </div>
        </section>
      </div>
      <section className={styles['button-info-container']}>
        <div className={styles.tabs}>
          <Button
            label="Driving"
            className={buildClassName(isDriving && styles.active, isZoocasaTheme && styles['zoocasa-theme'])}
            onClick={() => setIsDriving(true)}
          />
          <Button
            label="Walking"
            className={buildClassName(!isDriving && styles.active, isZoocasaTheme && styles['zoocasa-theme'])}
            onClick={() => setIsDriving(false)}
          />
        </div>
        {!!distance && distance !== 0 && travelTime && travelTime !== 0 &&
          <div className={styles['trip-info']}>
            <div className={styles['trip-info-title']}>
              {isDriving ? 'Driving' : 'Walking'}
            </div>
            <div className={styles['trip-info-stats']}>
              <div>
                {secondsFormatter(travelTime)}
              </div>
              <div className={styles['trip-info-kms']}>
                {Math.round(distance / 1000)}km
              </div>
            </div>
          </div>
        }
        {distance === 0 && travelTime === 0 && <div>Sorry, we could not calculate the transit travel time based on your location.</div>}
      </section>
      <div id={mapElementId} className={styles.map} />
    </div>
  );
}
